import { Component, inject, NgZone } from '@angular/core';
import { IonApp, IonRouterOutlet, Platform, IonModal, IonButton, IonImg } from '@ionic/angular/standalone';
import { register } from 'swiper/element/bundle';
import { environment } from 'src/environments/environment';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { CheckAppVersionService } from './providers/check-app-version.service';
import { CheckAppVersionRequest, CheckAppVersionResponse } from './models/check-app-version-req-res';

register();

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [IonImg, IonButton, IonModal, IonApp, IonRouterOutlet],
})
export class AppComponent {
  private platform: Platform = inject(Platform);
  private checkAppVersionService: CheckAppVersionService = inject(CheckAppVersionService);
  showUpdateModal = false;
  laterButton = false;
  updateUrl = '';

  constructor(private router: Router, private ngZone: NgZone) {
    this.loadGoogleMapsScript();
    this.setupDeepLinkHandlers();
    this.initializeApp();
  }

  async initializeApp() {
    await this.platform.ready();

    const platform = Capacitor.getPlatform(); // 'web' | 'ios' | 'android'

    if (platform === 'web') {
      return;
    }

    let currentVersion = '1.0.0';
    try {
      const info = await App.getInfo();
      currentVersion = info.version;
    } catch (error) {
      console.warn('Failed to get app version:', error);
    }

    const checkAppVersionRequest: CheckAppVersionRequest = {
      currentVersion,
      platform,
    };

    try {
      const checkAppVersionResponse: CheckAppVersionResponse = await this.checkAppVersionService.checkAppVersion(checkAppVersionRequest);
      if (checkAppVersionResponse.updateRequired && checkAppVersionResponse.updateType.toLowerCase() !== 'updated') {
        this.updateUrl = checkAppVersionResponse.appUrl;
        this.laterButton = checkAppVersionResponse.updateType.toLowerCase() === 'soft';
        this.showUpdateModal = true;
      }
    } catch (error) {
      console.warn('Version check failed:', error);
    }
  }
  closeUpdateModal() {
    this.showUpdateModal = false;
  }
  updateApp() {
    window.open(this.updateUrl, '_system');
  }

  loadGoogleMapsScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if ((window as any).google?.maps) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsApiKey}&loading=async&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject('Google Maps script load error');
      document.head.appendChild(script);
    });
  }

  async setupDeepLinkHandlers() {
    const result = await App.getLaunchUrl();
    if (result?.url) {
      this.handleDeepLink(result.url);
    }

    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.handleDeepLink(event.url);
    });
  }

  private handleDeepLink(url: string) {
    try {
      const parsedUrl = new URL(url);
      const path = parsedUrl.pathname;
      const projectId = parsedUrl.searchParams.get('projectId');

      if (path === '/project-details' && projectId) {
        this.ngZone.run(() => {
          this.router.navigate(['/project-details'], {
            queryParams: { projectId },
          });
        });
      }

      // Handle e-sign deep link
      // URL format: /esign/sign/{token}
      if (path.startsWith('/esign/sign/')) {
        const token = path.split('/esign/sign/')[1];
        if (token) {
          this.ngZone.run(() => {
            this.router.navigate(['/esign/sign', token]);
          });
        }
      }
    } catch (error) {
      console.error('Error parsing deep link URL:', error);
    }
  }
}
